// src/App.tsx
// Translations removed - Arabic only
import { Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";

// ===== Employee / internal area
import DefaultLayout from "./layout/DefaultLayout";
import EmployeeProfile from "./components/EmployeeProfile/EmployeeProfile";
import Dashboard from "./components/Dashboard/Dashboard";

// ===== Public area
import PublicLayout from "./layout/PublicLayout";
import About from "./components/pages/About";
import ServicesLanding from "./components/pages/services/ServicesLanding";
import SiteSearchResultsPage from "./components/pages/search/SiteSearchResultsPage";

import MosqueSearchPage from "./components/pages/mosque/MosqueSearchPage";
import MosqueDetailsPage from "./components/pages/mosque/MosqueDetailsPage";
import MosqueIncidentReportingPage from "./components/pages/mosque/MosqueIncidentReportingPage";
import MosqueEvaluationPage from "./components/pages/mosque/MosqueEvaluationPage";
import MosqueDonationDetailsPage from "./components/pages/mosque/MosqueDonationDetailsPage";
import MosqueInformationDonationPage from "./components/pages/mosque/MosqueInformationDonationPage";
import MosqueDonationContractPage from "./components/pages/mosque/MosqueDonationContractPage";
import MosqueDonationRequest from "./components/pages/mosque/MosqueDonationRequest";
import MosqueSearchService from "./components/pages/MosqueSearchService";
// MAP Page of Mosque 
import MosqueMapPage from "./components/pages/mosque/MosqueMapPage";

import LandListPage from "./components/pages/donations/LandListPage";
import LandDetailPage from "./components/pages/donations/LandDetailPage";
import AvailableLandsService from "./components/pages/AvailableLandsService";
import MosqueContributionService from "./components/pages/MosqueContributionService";
// Land Map Page
import LandMapPage from "./components/pages/donations/LandMapPage";


// ===== Benefaction (public)
import BenefactorLogin from "./components/pages/benefaction/BenefactorLogin";
import NafathLoginTestPage from "./components/pages/benefaction/NafathLoginTestPage";
import NafathIdentityPage from "./components/pages/benefaction/NafathIdentityPage";
import BenefactorList from "./components/pages/benefaction/BenefactorList";

// Benefaction Logged IN
import BenefactorRequestGen from "./components/pages/benefaction/Request/DonationRequestBase";
import BenefactorSignUp from "./components/pages/benefaction/BenefactorSignUp";
import BeneficiaryDashboardPage from "./components/pages/benefaction/BeneficiaryDashboardPage";
import UnifiedMyRequestsPage from "./components/pages/benefaction/UnifiedMyRequestsPage";
import UserProfilePage from "./components/pages/benefaction/UserProfilePage";

import MosqueDonationOpportunitiesMapPage from "./components/pages/mosque/MosqueDonationOpportunitiesMapPage";

// ===== NEW: WordPress bridge (renders nothing; only message wiring)
import WpBridge from "./components/embed/wpBridge"; // your tsx bridge file
import { AUTH_BRIDGE, isEmbedMode } from "./config";

// ===== Auth helpers (existing)
import {
  isAuthenticated,
  getAuthToken,
  clearAuthToken, // used as our minimal "logout" trigger
} from "./services/authService";

import MosqueDonationOpportunitiesPage from "./components/pages/mosque/MosqueDonationOpportunitiesPage";
import MosqueDonationOpportunityDetailsPage from "./components/pages/mosque/MosqueDonationOpportunityDetailsPage";
import ResidentOrBenefactorAccountCreation from "./components/pages/LoginAndSingupNew/ResidentOrBenefactorAccountCreation";

/**
 * Decode display name from JWT (if present).
 * - We avoid changing your existing auth flow.
 * - We just *read* what you already store in the token.
 * - Tries multiple common claim keys for safety.
 */
function getDisplayNameFromJwt() {
  try {
    const token = getAuthToken?.();
    if (!token || typeof token !== "string" || token.split(".").length < 2) return null;

    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const payload = JSON.parse(json);

    // Try a few likely claim names you've used across modules
    return (
      payload?.displayName ||
      payload?.name ||
      payload?.display_name ||
      payload?.preferred_username ||
      null
    );
  } catch {
    return null;
  }
}

/**
 * Bridge snapshot reader:
 * - Source of truth: your existing auth state.
 * - No writes here; just shape auth into a tiny snapshot for WP.
 */
function getAuthSnapshot() {
  const loggedIn = !!isAuthenticated?.();
  const displayName = loggedIn ? getDisplayNameFromJwt() : null;
  return { loggedIn, displayName };
}

/**
 * Minimal logout trigger for the bridge:
 * - We call your existing clearAuthToken().
 * - Then notify listeners that auth changed (so WP header updates instantly).
 * - We do NOT change any navigation/UX here to avoid touching existing flows.
 */
function requestLogout() {
  try {
    clearAuthToken?.();
  } finally {
    // Notify the bridge (and anyone else) that auth state changed
    window.dispatchEvent(
      new CustomEvent(AUTH_BRIDGE.authChangedEventName, { detail: getAuthSnapshot() })
    );
  }
}

function App() {
  // Only activate the bridge when we are embedded (iFrame) via ?embed=1
  const embed = isEmbedMode();

  return (
    <>
      {/* ===== Your existing routes stay exactly as-is ===== */}
      <Routes>
        {/* ===== Public site (shared nav/header via PublicLayout; ensure <Outlet/> inside it) ===== */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<About />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<ServicesLanding />} />
          <Route path="/search" element={<SiteSearchResultsPage />} />
          <Route path="/mosquesearch" element={<MosqueSearchPage />} />
          <Route path="/mosques/:id" element={<MosqueDetailsPage />} />
          <Route path="/mosques/:id/report" element={<MosqueIncidentReportingPage />} />
          <Route path="/mosques/:id/evaluate" element={<MosqueEvaluationPage />} />
          <Route path="/service/mosque-search" element={<MosqueSearchService />} />
          <Route path="/service/available-lands" element={<AvailableLandsService />} />
          <Route path="/service/mosque-contribution" element={<MosqueContributionService />} />
          <Route path="/mosques/map" element={<MosqueMapPage />} />

          <Route path="/mosque/donation-opportunities" element={<MosqueDonationOpportunitiesPage />} />
          <Route path="/mosque/donation-details/:id" element={<MosqueDonationDetailsPage />} />
          <Route path="/mosque/information-donation" element={<MosqueInformationDonationPage />} />
          <Route path="/mosque/donation-contract" element={<MosqueDonationContractPage />} />
          <Route path="/mosque/donation-request/:id" element={<MosqueDonationRequest />} />

          {/* ✅ NEW: Opportunities Map (raw JSON API) */}
          <Route
            path="/mosque/donation-opportunities-map"
            element={<MosqueDonationOpportunitiesMapPage />}
          />

          
          <Route path="/donations/mosques/opportunities/:id" element={<MosqueDonationOpportunityDetailsPage />}/>

          {/* Beneficiary dashboard (protected: redirects to login if not authenticated) */}
          <Route path="/my-dashboard" element={<BeneficiaryDashboardPage />} />
          {/* Unified my requests (two tabs: maintenance + lands) */}
          <Route path="/my-requests" element={<UnifiedMyRequestsPage />} />
          {/* User profile (read-only data from registration) */}
          <Route path="/my-profile" element={<UserProfilePage />} />
          {/* Legacy: redirect old mosque my-requests to unified page */}
          <Route path="/mosque/my-requests" element={<Navigate to="/my-requests?tab=maintenance" replace />} />
          {/* /login redirects to BenefactorLogin (project uses benefactor login only) */}
          <Route path="/login" element={<Navigate to="/benefaction/login" replace />} />

          <Route path="/resident-signup" element={<ResidentOrBenefactorAccountCreation />} />

          {/* Benefaction (public) */}
          <Route path="/benefaction">
            <Route index element={<Navigate to="lands" replace />} />
            <Route path="lands" element={<LandListPage />} />
            <Route path="lands/:landId" element={<LandDetailPage />} />
            <Route path="login" element={<BenefactorLogin />} />
            {process.env.NODE_ENV === "development" && (
              <>
                <Route path="login-nafath" element={<NafathLoginTestPage />} />
                <Route path="nafath-identity" element={<NafathIdentityPage />} />
              </>
            )}

            {/* ✅ NEW clean route */}
            <Route path="benefactors" element={<BenefactorList />} />

            {/* ✅ Keep old casing route working */}
            <Route path="BenefactorList" element={<Navigate to="/benefaction/benefactors" replace />} />

            <Route path="request/:landId" element={<BenefactorRequestGen />} />

            {/* keep signup route as-is for now */}
            <Route path="signup" element={<BenefactorSignUp />} />

            <Route path="requests" element={<Navigate to="/my-requests?tab=lands" replace />} />

            <Route path="lands/map" element={<LandMapPage />} />

          </Route>
          {/* Legacy redirects (keep if old links exist) */}
          <Route path="/donations/lands" element={<Navigate to="/benefaction/lands" replace />} />
          <Route path="/donations/login" element={<BenefactorLogin />} />
          <Route path="/donations/*" element={<Navigate to="/benefaction/lands" replace />} />
        </Route>

        {/* ===== Employee / internal area (protected layout) ===== */}
        <Route path="/dashboard" element={
          <LanguageProvider>
            <DefaultLayout />
          </LanguageProvider>
        }>
          <Route index element={<Dashboard />} />
          <Route path="profile" element={<EmployeeProfile />} />
          {/* add other protected routes here */}
        </Route>

        {/* ===== Fallback ===== */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* ===== NEW: Mount the WP bridge ONLY in embed mode (no impact on normal app) ===== */}
      {embed && (
        <WpBridge
          parentOrigin={AUTH_BRIDGE.parentOrigin} // from config.js (WORDPRESS_BASE_URL)
          getAuthSnapshot={getAuthSnapshot}
          requestLogout={requestLogout}
          enabled={true}
          debug={false} // set true temporarily if you want console logs
          authChangedEventName={AUTH_BRIDGE.authChangedEventName}
        />
      )}
    </>
  );
}

export default App;

